"use client";

import { ArrowRight, ShieldCheck, Zap, RefreshCw, Users } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheInfinityAdvantage() {
  const {
    ref: titleRef,
    isVisible: titleVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const features = [{
    number: "01",
    title: "AI-Driven Protection",
    description: "Our proprietary neural networks analyze traffic patterns in real-time, identifying anomalies that traditional firewalls miss. We don't just react; we predict and neutralize threats before they breach your perimeter.",
    Icon: ShieldCheck
  }, {
    number: "02",
    title: "Proactive Threat Prevention",
    description: "We deploy automated defensive measures that evolve alongside emerging cyber threats. By hardening your infrastructure continuously, we ensure your digital assets remain resilient against sophisticated attacks.",
    Icon: Zap
  }, {
    number: "03",
    title: "Fast Incident Response",
    description: "In the event of a security event, our rapid-response protocols trigger instantly. We minimize downtime and data exposure, ensuring your business operations remain stable and secure under pressure.",
    Icon: RefreshCw
  }, {
    number: "04",
    title: "Trusted Expert Support",
    description: "Gain access to a dedicated team of cybersecurity veterans. We provide 24/7 monitoring and strategic guidance, acting as an extension of your internal IT department to keep you compliant and protected.",
    Icon: Users
  }];
  return <section data-section-id="5265" id="the-infinity-advantage" className="bg-background text-foreground relative overflow-hidden py-24 md:py-32 lg:py-40" style={{
    backgroundImage: "radial-gradient(circle, hsl(185 100% 50% / 0.04) 1px, transparent 1px)",
    backgroundSize: "24px 24px"
  }}>
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-5">
            <div ref={titleRef} className={`lg:sticky lg:top-32 transition-all duration-700 ease-out ${titleVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <p className="font-mono text-xs tracking-[0.2em] text-primary uppercase mb-8">
                [ 06 / ADVANTAGE ]
              </p>
              <h2 className="font-default font-semibold text-5xl sm:text-6xl lg:text-7xl xl:text-[80px] leading-[0.95] tracking-tighter text-foreground mb-8">
                The Infinity Advantage
              </h2>
              <p className="text-base lg:text-lg text-muted-foreground leading-relaxed max-w-md mb-10">
                Four core pillars define our approach to modern cybersecurity. We combine advanced AI intelligence with human expertise to build an impenetrable shield for your business.
              </p>
              <Link to="/WhyUsPage" className="inline-flex items-center gap-3 font-mono text-xs tracking-[0.2em] uppercase text-primary hover:text-primary/80 transition-colors group">
                <span className="border-b border-primary/40 group-hover:border-primary pb-1">
                  Our Security Philosophy
                </span>
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <div className="flex flex-col">
              {features.map((feature, i) => {
              const {
                ref,
                isVisible
              } = useRevealOnScroll<HTMLDivElement>({
                threshold: 0.25
              });
              const Icon = feature.Icon;
              return <div key={i} data-index={i} ref={ref} className={`group relative py-10 md:py-12 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
                transitionDelay: `${i * 80}ms`
              }}>
                    {}
                    <div className={`absolute top-0 left-0 h-px bg-primary/70 transition-all duration-1000 ease-out group-hover:bg-primary group-hover:shadow-[0_0_12px_hsl(185_100%_50%/0.6)] ${isVisible ? "w-full" : "w-0"}`} style={{
                  transitionDelay: `${i * 80 + 100}ms`
                }} />

                    <div className="flex items-start justify-between gap-6">
                      <div className="flex-1">
                        <div className="flex items-baseline gap-6 mb-5">
                          <span className="font-mono text-4xl md:text-5xl lg:text-[64px] font-light text-muted-foreground/70 leading-none tracking-tight transition-transform duration-500 group-hover:-translate-y-1 group-hover:text-primary">
                            {feature.number}
                          </span>
                          <h3 className="font-default font-semibold text-2xl md:text-3xl lg:text-[36px] leading-tight tracking-tight text-foreground">
                            {feature.title}
                          </h3>
                        </div>
                        <p className="text-sm md:text-base text-muted-foreground leading-relaxed max-w-xl pl-0 md:pl-[88px] lg:pl-[104px]">
                          {feature.description}
                        </p>
                      </div>

                      <div className="flex-shrink-0 pt-2">
                        <div className="h-12 w-12 md:h-14 md:w-14 rounded-lg border border-border/60 flex items-center justify-center transition-all duration-500 group-hover:rotate-[8deg] group-hover:border-primary/60">
                          <Icon className="h-5 w-5 md:h-6 md:w-6 text-foreground/70 group-hover:text-primary transition-colors duration-500" strokeWidth={1} />
                        </div>
                      </div>
                    </div>
                  </div>;
            })}

              {}
              <div className="h-px bg-border/40 w-full" />
            </div>

            {}
            <div className="mt-12 lg:hidden">
              <Button asChild variant="outline" className="font-mono text-xs tracking-[0.2em] uppercase border-primary/40 text-primary hover:bg-primary/10 hover:text-primary rounded-lg">
                <Link to="/WhyUsPage">
                  Our Security Philosophy
                  <ArrowRight className="h-3.5 w-3.5 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}